import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { ChevronRight, ArrowLeft, Save, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', description: '', equipement_disponible: '', capacite: '',
  prix_demi_journee: '', prix_journee: '',
  numero_voie: '', indice_repetition: '', type_voie: '', libelle_voie: '',
  code_postal: '', ville: '', pays: 'France FR', lieu_dit: '',
  conformite_pmr: false, description_acces: '',
  contact_nom: '', contact_tel: '', contact_email: '', notes: '',
  borne_wifi: '', code_wifi: '', paperboard: false, projecteur: false, imprimante: false, tableau_noir: false,
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-lg font-bold text-slate-900 mt-8 mb-5 first:mt-0">{children}</h2>
);

const inputCls = "w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none transition-colors";
const selectCls = "w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none appearance-none";

const Checkbox = ({ checked, onChange, label, testId }) => (
  <div className="flex items-center gap-3 h-10">
    <button onClick={onChange} className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${checked ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`} data-testid={testId}>
      {checked && <Check className="h-3 w-3 text-white" />}
    </button>
    <span className="text-sm text-slate-600">{label}</span>
  </div>
);

const LieuDetailPage = () => {
  const { lieuId } = useParams();
  const navigate = useNavigate();
  const isNew = !lieuId || lieuId === 'nouveau';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });

  const fetchLieu = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/donnees-lieux/${lieuId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, ...data }));
      } else {
        toast.error("Lieu non trouvé");
        navigate('/donnees/lieux');
      }
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [lieuId, isNew, navigate]);

  useEffect(() => { fetchLieu(); }, [fetchLieu]);

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/donnees-lieux` : `${API}/donnees-lieux/${lieuId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isNew ? 'Lieu créé' : 'Lieu mis à jour');
        if (isNew && data.lieu_id) navigate(`/donnees/lieux/${data.lieu_id}`, { replace: true });
      } else { toast.error("Erreur lors de l'enregistrement"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce lieu ?")) return;
    await fetch(`${API}/donnees-lieux/${lieuId}`, { method: 'DELETE', credentials: 'include' });
    toast.success("Lieu supprimé");
    navigate('/donnees/lieux');
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-slate-100 rounded animate-pulse" />
          <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="lieu-detail-page">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="cursor-pointer hover:text-violet-500 transition-colors" onClick={() => navigate('/donnees/lieux')}>Lieux de formation</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">{isNew ? 'Nouveau lieu' : form.name}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/donnees/lieux')} className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <h1 className="text-2xl font-black text-slate-900" data-testid="page-title">
              {isNew ? 'Nouveau lieu' : form.name}
            </h1>
          </div>
          {!isNew && (
            <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="delete-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> Supprimer
            </Button>
          )}
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>

        {/* Informations générales */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <SectionTitle>Informations générales</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Nom *">
              <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} data-testid="input-name" />
            </Field>
            <Field label="Description">
              <input className={inputCls} value={form.description} onChange={e => set('description', e.target.value)} data-testid="input-description" />
            </Field>
            <Field label="Équipement disponible">
              <input className={inputCls} value={form.equipement_disponible} onChange={e => set('equipement_disponible', e.target.value)} data-testid="input-equipement" />
            </Field>
            <Field label="Capacité">
              <input className={inputCls} value={form.capacite} onChange={e => set('capacite', e.target.value)} data-testid="input-capacite" />
            </Field>
            <Field label="Prix 1/2 journée (€)">
              <input className={inputCls} value={form.prix_demi_journee} onChange={e => set('prix_demi_journee', e.target.value)} data-testid="input-prix-demi" />
            </Field>
            <Field label="Prix journée (€)">
              <input className={inputCls} value={form.prix_journee} onChange={e => set('prix_journee', e.target.value)} data-testid="input-prix-journee" />
            </Field>
          </div>
        </div>

        {/* Adresse */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <SectionTitle>Adresse</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Numéro de la voie">
              <input className={inputCls} value={form.numero_voie} onChange={e => set('numero_voie', e.target.value)} data-testid="input-numero-voie" />
            </Field>
            <Field label="Indice de répétition">
              <select className={selectCls} value={form.indice_repetition} onChange={e => set('indice_repetition', e.target.value)} data-testid="input-indice">
                <option value="">—</option>
                <option value="bis">bis</option>
                <option value="ter">ter</option>
                <option value="quater">quater</option>
              </select>
            </Field>
            <Field label="Type de la voie">
              <select className={selectCls} value={form.type_voie} onChange={e => set('type_voie', e.target.value)} data-testid="input-type-voie">
                <option value="">—</option>
                <option value="Rue">Rue</option>
                <option value="Avenue">Avenue</option>
                <option value="Boulevard">Boulevard</option>
                <option value="Place">Place</option>
                <option value="Chemin">Chemin</option>
                <option value="Impasse">Impasse</option>
                <option value="Route">Route</option>
                <option value="Allée">Allée</option>
              </select>
            </Field>
            <Field label="Libellé de la voie">
              <input className={inputCls} value={form.libelle_voie} onChange={e => set('libelle_voie', e.target.value)} data-testid="input-libelle-voie" />
            </Field>
            <Field label="Code postal">
              <input className={inputCls} value={form.code_postal} onChange={e => set('code_postal', e.target.value)} data-testid="input-code-postal" />
            </Field>
            <Field label="Ville">
              <input className={inputCls} value={form.ville} onChange={e => set('ville', e.target.value)} data-testid="input-ville" />
            </Field>
            <Field label="Pays">
              <select className={selectCls} value={form.pays} onChange={e => set('pays', e.target.value)} data-testid="input-pays">
                <option value="France FR">France FR</option>
                <option value="CM">Cameroun CM</option>
                <option value="BE">Belgique BE</option>
                <option value="CH">Suisse CH</option>
              </select>
            </Field>
            <Field label="Lieu dit ou service particulier de distribution">
              <input className={inputCls} value={form.lieu_dit} onChange={e => set('lieu_dit', e.target.value)} data-testid="input-lieu-dit" />
            </Field>
            <Checkbox checked={form.conformite_pmr} onChange={() => set('conformite_pmr', !form.conformite_pmr)} label="Conformité réglementaire de l'accès aux personnes à mobilité réduite" testId="input-pmr" />
            <Field label="Description des moyens d'accès" className="col-span-2">
              <textarea className="w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none min-h-[60px]" value={form.description_acces} onChange={e => set('description_acces', e.target.value)} data-testid="input-description-acces" />
            </Field>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <SectionTitle>Contact</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Nom du contact">
              <input className={inputCls} value={form.contact_nom} onChange={e => set('contact_nom', e.target.value)} data-testid="input-contact-nom" />
            </Field>
            <Field label="Tél du contact">
              <input className={inputCls} value={form.contact_tel} onChange={e => set('contact_tel', e.target.value)} data-testid="input-contact-tel" />
            </Field>
            <Field label="Email du contact">
              <input className={inputCls} type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} data-testid="input-contact-email" />
            </Field>
            <Field label="Notes" className="col-span-2">
              <textarea className="w-full border-0 border-b border-slate-300 rounded-none bg-slate-50/50 px-3 py-2.5 text-sm focus:ring-0 focus:border-violet-500 outline-none min-h-[60px]" value={form.notes} onChange={e => set('notes', e.target.value)} data-testid="input-notes" />
            </Field>
          </div>
        </div>

        {/* Champs personnalisés */}
        <div className="bg-slate-50/80 border border-slate-200 rounded-2xl p-8">
          <SectionTitle>Champs personnalisés</SectionTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <Field label="Borne Wi-Fi">
              <input className={inputCls} value={form.borne_wifi} onChange={e => set('borne_wifi', e.target.value)} data-testid="input-borne-wifi" />
            </Field>
            <Field label="Code Wi-Fi">
              <input className={inputCls} value={form.code_wifi} onChange={e => set('code_wifi', e.target.value)} data-testid="input-code-wifi" />
            </Field>
            <Checkbox checked={form.paperboard} onChange={() => set('paperboard', !form.paperboard)} label="Paperboard" testId="input-paperboard" />
            <Checkbox checked={form.projecteur} onChange={() => set('projecteur', !form.projecteur)} label="Projecteur" testId="input-projecteur" />
            <Checkbox checked={form.imprimante} onChange={() => set('imprimante', !form.imprimante)} label="Imprimante" testId="input-imprimante" />
            <Checkbox checked={form.tableau_noir} onChange={() => set('tableau_noir', !form.tableau_noir)} label="Tableau noir" testId="input-tableau-noir" />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LieuDetailPage;
